// Device-side ePOS-Print (Epson network printing) HTTP transport.
//
// Pure transport module — no React/UI concerns. Talks directly to a LAN
// printer's ePOS-Print HTTP endpoint. Under Capacitor, `CapacitorHttp` (see
// capacitor.config.ts) transparently routes `fetch` through the native HTTP
// layer, which is what makes plain-HTTP requests to a LAN device work from
// an iOS/Android webview without CORS or ATS issues — no plugin, no
// special-casing needed here.
//
// Contract: nothing in this module ever throws or rejects. A print must
// never take the till down, and a probe must always produce something the
// portal can show the tenant. Every failure mode (refused connection,
// timeout, non-2xx response, wrong device entirely) resolves to a plain
// result object instead.

// Raw ESC/POS is the fallback for printers with no ePOS service (TM-T20X
// and similar). The probe reports on both so setup can pick automatically.
import { probeRawPort, RAW_PORT, RAW_OPEN, RAW_UNAVAILABLE } from './escposClient';

export const EPOS_PATH = '/cgi-bin/epos/service.cgi';

// Epson's ePOS-Print CGI addresses a specific device behind the endpoint, and
// REFUSES a request that does not name one — the printer answers with an HTTP
// error rather than printing. `local_printer` is the built-in id for the
// printer itself; a print server fronting several devices exposes
// local_printer2, local_printer3, ... instead.
//
// Omitting this is what made every POST fail against a real TM-m30 on
// 2026-08-26: the test print reported `http_error`, and the probe — whose
// status POST takes this same path — reported "reachable, but no ePOS",
// which read as an unsupported printer rather than a malformed URL.
export const DEFAULT_DEVICE_ID = 'local_printer';

// Firmware-side wait for the print job, in ms. Documented alongside devid and
// sent with it; the printer applies it to the job, independent of our own
// client-side AbortController timeout.
const DEVICE_TIMEOUT_MS = 10000;

// A minimal, well-formed ePOS-Print request with no print commands inside
// it. Used only to probe whether a device speaks ePOS-Print at all — an
// empty <epos-print> body asks the firmware to respond without putting
// anything on paper.
const STATUS_PROBE_XML = '<?xml version="1.0" encoding="utf-8"?>'
    + '<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">'
    + '<s:Body><epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print"/></s:Body>'
    + '</s:Envelope>';

// Epson's entire thermal receipt/POS printer line is branded "TM-<model>"
// (TM-m30II, TM-T88VII, TM-T20III, ...). Looking for that token in a
// device's own config/status page is a simple, real-world way to identify
// the model without needing a structured API for it.
const MODEL_PATTERN = /\bTM-[A-Za-z0-9]+\b/;

/**
 * Build the documented ePOS-Print endpoint URL for a printer, including the
 * mandatory devid — see DEFAULT_DEVICE_ID for why it is not optional.
 * @param {string} ip
 * @param {number} [port] defaults to 80
 * @param {{devid?: string}} [opts]
 * @returns {string}
 */
export function eposUrl(ip, port, opts = {}) {
    const resolvedPort = port === undefined || port === null ? 80 : port;
    const devid = opts.devid || DEFAULT_DEVICE_ID;
    return `http://${ip}:${resolvedPort}${EPOS_PATH}`
        + `?devid=${encodeURIComponent(devid)}&timeout=${DEVICE_TIMEOUT_MS}`;
}

async function fetchWithTimeout(url, options, timeoutMs) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, { ...options, signal: controller.signal });
    } finally {
        clearTimeout(timer);
    }
}

async function safeText(response) {
    try {
        return await response.text();
    } catch (e) {
        return '';
    }
}

// Parses an ePOS-Print <response .../> element out of a response body,
// tolerant of both a bare root element and one wrapped in a SOAP envelope.
// Returns null when the body isn't a recognisable ePOS response at all
// (e.g. an HTML page from the wrong IP, or a non-Epson device).
function parseEposResponse(text) {
    if (!text || !text.trim()) {
        return null;
    }

    let doc;
    try {
        doc = new DOMParser().parseFromString(text, 'text/xml');
    } catch (e) {
        return null;
    }

    if (!doc || doc.getElementsByTagName('parsererror').length > 0) {
        return null;
    }

    const responseEl = doc.getElementsByTagName('response')[0];
    if (!responseEl) {
        return null;
    }

    const success = (responseEl.getAttribute('success') || '').toLowerCase() === 'true';
    const code = responseEl.getAttribute('code');

    return { success, code: code ? code : null };
}

/**
 * POST an ePOS-Print XML envelope to a printer. Never throws — every
 * failure mode resolves to a result object instead.
 * @param {string} ip
 * @param {number} port
 * @param {string} xml
 * @param {{timeoutMs?: number}} [opts]
 * @returns {Promise<{success: boolean, code: string|null, reason: string|null}>}
 */
export async function sendToPrinter(ip, port, xml, opts = {}) {
    const { timeoutMs = 5000, devid } = opts;
    const url = eposUrl(ip, port, { devid });

    let response;
    try {
        response = await fetchWithTimeout(url, {
            method: 'POST',
            headers: { 'Content-Type': 'text/xml; charset=utf-8', SOAPAction: '""' },
            body: xml,
        }, timeoutMs);
    } catch (err) {
        if (err && err.name === 'AbortError') {
            return { success: false, code: null, reason: 'timeout' };
        }
        return { success: false, code: null, reason: 'network_error' };
    }

    if (!response) {
        return { success: false, code: null, reason: 'network_error' };
    }

    if (!response.ok) {
        return { success: false, code: null, reason: 'http_error' };
    }

    const text = await safeText(response);
    const parsed = parseEposResponse(text);

    if (!parsed) {
        return { success: false, code: null, reason: 'invalid_response' };
    }

    if (parsed.success) {
        return { success: true, code: parsed.code, reason: null };
    }

    return { success: false, code: parsed.code, reason: 'device_error' };
}

/**
 * Probe a LAN address for an Epson ePOS-Print-capable printer. Runs two
 * independent checks — a GET of the device's own config page (for a model
 * name) and an ePOS status POST — and reports both independently so the
 * caller can distinguish "wrong IP, nothing there" from "reachable, but
 * this device doesn't speak ePOS-Print". Never throws.
 * @param {string} ip
 * @param {number} port
 * @param {{timeoutMs?: number}} [opts]
 * @returns {Promise<{reachable: boolean, eposAvailable: boolean, model: string|null, raw: string|null}>}
 */
export async function probePrinter(ip, port, opts = {}) {
    const { timeoutMs = 4000 } = opts;

    let reachable = false;
    let model = null;
    let raw = null;

    try {
        const res = await fetchWithTimeout(`http://${ip}/`, { method: 'GET' }, timeoutMs);
        if (res) {
            reachable = true;
            const text = await safeText(res);
            raw = text || null;
            if (text) {
                const match = text.match(MODEL_PATTERN);
                if (match) {
                    model = match[0];
                }
            }
        }
    } catch (e) {
        // Nothing answered at that address — leave reachable as-is.
    }

    let eposAvailable = false;
    try {
        const eposResult = await sendToPrinter(ip, port, STATUS_PROBE_XML, { timeoutMs });
        if (eposResult.reason !== 'network_error' && eposResult.reason !== 'timeout') {
            reachable = true;
        }
        eposAvailable = eposResult.reason === null || eposResult.reason === 'device_error';
    } catch (e) {
        // sendToPrinter never throws by contract, but guard anyway — a
        // probe must always resolve to something the portal can show.
    }

    // Only worth asking about raw printing when ePOS said no. An
    // ePOS-capable printer should keep using ePOS: it returns a status
    // document, so we learn about paper-out and cover-open. Port 9100 is
    // write-only and tells us nothing beyond "the bytes were accepted".
    // rawStatus is tri-state: 'open' | 'closed' | 'unavailable'. The last
    // means we could not ask (no native plugin), which must never be
    // reported as "this printer can't do raw printing".
    let rawStatus = RAW_UNAVAILABLE;
    if (!eposAvailable) {
        rawStatus = await probeRawPort(ip, RAW_PORT, { timeoutMs });
        if (rawStatus === RAW_OPEN) {
            reachable = true;
        }
    }

    return {
        reachable,
        eposAvailable,
        rawAvailable: rawStatus === RAW_OPEN,
        rawStatus,
        model,
        raw,
    };
}
